import { DsButton, DsDialog, DsEmptyState, DsIcons, DsSpinner, DsSuccessView, DsText, formatCountdown } from '@/ds'
import { checkoutStrings } from '@/features/payments/strings'
import type { CheckoutPhase, CheckoutState } from '@/features/payments/useCheckout'

export interface CheckoutStatusCopy {
  confirmingTitle?: string
  confirmingDescription?: string
  successTitle?: string
  successDescription?: string
  failedDescription?: string
  unconfirmedDescription?: string
}

export interface CheckoutStatusProps extends CheckoutState {
  copy?: CheckoutStatusCopy
  onRetry?: () => void
  onDone?: () => void
  onClose?: () => void
  doneLabel?: string
}

const TERMINAL: CheckoutPhase[] = ['success', 'processing', 'failed', 'cancelled', 'unconfirmed']

/** Not dismissible while busy (the mobile "don't close the app" rule). */
export function CheckoutStatus({ phase, error, countdown, copy = {}, onRetry, onDone, onClose, doneLabel = checkoutStrings.done }: CheckoutStatusProps) {
  const open = phase !== 'idle' && phase !== 'gateway'
  const terminal = TERMINAL.includes(phase)
  const close = () => (terminal ? (phase === 'success' ? onDone?.() : onClose?.()) : undefined)
  return (
    <DsDialog open={open} onClose={close} title={titleFor(phase, copy)} canDismiss={terminal} tone={phase === 'failed' ? 'danger' : phase === 'unconfirmed' || phase === 'processing' ? 'warning' : phase === 'success' ? 'success' : 'brand'} icon={phase === 'success' ? DsIcons.check : undefined}>
      {phase === 'preparing' && <Busy text={checkoutStrings.preparing} />}
      {phase === 'confirming' && (
        <div className="flex flex-col items-center gap-3 text-center">
          <DsSpinner size={32} />
          <DsText variant="body" muted>{copy.confirmingDescription ?? checkoutStrings.confirming}</DsText>
          {countdown !== null && <DsText variant="caption" muted className="tabular-nums">{checkoutStrings.countdown(formatCountdown(countdown))}</DsText>}
          <DsText variant="caption" muted>{checkoutStrings.dontClose}</DsText>
        </div>
      )}
      {phase === 'success' && <DsSuccessView title={copy.successTitle ?? checkoutStrings.successTitle} description={copy.successDescription} primaryLabel={doneLabel} onPrimary={onDone} className="py-2" />}
      {phase === 'processing' && <DsEmptyState icon={DsIcons.hourglass} tone="warning" title={checkoutStrings.processingTitle} description={checkoutStrings.processingDescription} actionLabel={doneLabel} onAction={onDone} compact />}
      {(phase === 'failed' || phase === 'cancelled') && (
        <div className="flex flex-col gap-4">
          <DsText variant="body" muted>{error ?? copy.failedDescription ?? checkoutStrings.failedDescription}</DsText>
          <div className="flex gap-3">
            <DsButton variant="outline" size="lg" label={checkoutStrings.close} onClick={onClose} className="flex-1" />
            {onRetry && <DsButton size="lg" label={checkoutStrings.tryAgain} onClick={onRetry} className="flex-1" />}
          </div>
        </div>
      )}
      {phase === 'unconfirmed' && (
        <div className="flex flex-col gap-4">
          <DsText variant="body" muted>{error ?? copy.unconfirmedDescription ?? checkoutStrings.leasePaymentUnconfirmed}</DsText>
          <DsButton size="lg" label={checkoutStrings.close} onClick={onClose} fullWidth />
        </div>
      )}
    </DsDialog>
  )
}

function Busy({ text }: { text: string }) {
  return (
    <div className="flex flex-col items-center gap-3 py-2 text-center" role="status">
      <DsSpinner size={32} />
      <DsText variant="body" muted>{text}</DsText>
    </div>
  )
}

function titleFor(phase: CheckoutPhase, copy: CheckoutStatusCopy): string {
  switch (phase) {
    case 'preparing':
      return checkoutStrings.preparing
    case 'confirming':
      return copy.confirmingTitle ?? checkoutStrings.agreementInProgress
    case 'success':
      return copy.successTitle ?? checkoutStrings.successTitle
    case 'processing':
      return checkoutStrings.processingTitle
    case 'failed':
      return checkoutStrings.failedTitle
    case 'cancelled':
      return checkoutStrings.cancelledTitle
    case 'unconfirmed':
      return checkoutStrings.unconfirmedTitle
    default:
      return ''
  }
}
